/**
 * Propagate selected furnizor pricing fields from one product to peer products
 * of the same supplier (draft-save flow).
 */
import { cloneDeep } from 'lodash';
import {
    buildCategoryAssignmentsFromSystems,
    getProductMainCategories,
} from '@/data/productCategories';
import {
    createDraftEntry,
    findDraftByProductCode,
    resolveDraftAction,
} from '@/utils/priceListDraftQueue';
import { CURRENCY_OPTIONS } from '@/data/manufacturerCodes';
import {
    builderFormToCalculatorInput,
    calculateProductPricing,
    DEFAULT_PRICING_SETTINGS,
    formatNumericInputValue,
    parseNumericInput,
    persistActiveFurnizorPricingFromRoot,
    applyFurnizorPricingToRoot,
    ratioToPercentageInput,
    roundMoney,
} from '@/utils/priceListProductCalculator';
import {
    builderFormToSavePayload,
    legacyProductToBuilderForm,
    pricingDetailToBuilderForm,
} from '@/utils/pricingDetailMapper';
import { cloneTransportCostBreakdown } from '@/utils/transportCostBreakdown';
import { cloneStampDutyBreakdown } from '@/utils/stampDutyBreakdown';
import {
    buildDraftPricingBaselinesFromPreEditForm,
    mergeDraftPricingBaselinesWithExisting,
    pricingFieldValuesEqual,
} from '@/utils/priceListPricingFieldBaselines';
import {
    applyPricingFieldEdit,
    syncMarkupActualToReference,
} from '@/utils/priceListPricingFieldSync';
import { includesFolded } from '@/utils/textSearch';

/** @typedef {{ key: string, label: string, needsPricingRecalc?: boolean, productLevel?: boolean }} PropagateFieldDef */

/** @type {PropagateFieldDef[]} */
export const PROPAGATE_FIELD_DEFINITIONS = [
    {
        key: 'transportCostRate',
        label: 'Cost transport + alte taxe',
        needsPricingRecalc: true,
    },
    {
        key: 'currency',
        label: 'Monedă',
        needsPricingRecalc: true,
    },
    {
        key: 'paymentTermDays',
        label: 'Termen plată',
    },
    {
        key: 'notes',
        label: 'Note furnizor',
    },
    {
        key: 'markupReference',
        label: 'Adaos referință',
    },
    {
        key: 'markupActual',
        label: 'Adaos actual',
        needsPricingRecalc: true,
    },
    {
        key: 'lockMarkupToReference',
        label: 'Adaos actual = adaos referință',
        needsPricingRecalc: true,
    },
    {
        key: 'stampDuty',
        label: 'Taxă de timbru',
        needsPricingRecalc: true,
    },
    {
        key: 'nomenclatureCode',
        label: 'Cod nomenclator',
        productLevel: true,
    },
];

const FIELD_BY_KEY = PROPAGATE_FIELD_DEFINITIONS.reduce((map, field) => {
    map[field.key] = field;
    return map;
}, {});

/**
 * @param {string} key
 * @returns {PropagateFieldDef|null}
 */
export function getPropagateField(key) {
    return FIELD_BY_KEY[key] || null;
}

/**
 * @param {unknown} value
 * @returns {string}
 */
function normalizeMatchLabel(value) {
    return String(value || '').trim().toLowerCase();
}

/**
 * Normalize codes so "03" and "3" match.
 * @param {unknown} code
 * @returns {string}
 */
function normalizeMatchCode(code) {
    const raw = String(code || '').trim().toLowerCase();
    if (!raw) {
        return '';
    }
    if (/^\d+$/.test(raw)) {
        return String(Number(raw));
    }
    return raw;
}

/**
 * @param {Record<string, unknown>|null|undefined} pricing
 * @param {Record<string, unknown>} [context] manufacturer fallback from product form/item
 * @returns {{
 *   type: 'furnizor'|'manufacturer',
 *   furnizorId: (number|string|null),
 *   furnizorCode: string,
 *   furnizorName: string,
 *   manufacturerCode: string,
 *   manufacturerName: string,
 *   label: string,
 * }|null}
 */
export function getFurnizorIdentity(pricing, context = {}) {
    if (!pricing || typeof pricing !== 'object') {
        return null;
    }

    const manufacturerCode = String(
        context.manufacturerCode
            || context.manufacturer_code
            || pricing.manufacturerCode
            || pricing.manufacturer_code
            || '',
    ).trim();
    const manufacturerName = String(
        context.manufacturerName
            || context.manufacturer_name
            || pricing.manufacturerName
            || pricing.manufacturer_name
            || '',
    ).trim();

    const usesManufacturer = Boolean(
        pricing.usesManufacturerAsFurnizor
            ?? pricing.uses_manufacturer_as_furnizor,
    );

    if (usesManufacturer) {
        const label = manufacturerName || manufacturerCode || 'Producător';
        return {
            type: 'manufacturer',
            furnizorId: null,
            furnizorCode: '',
            furnizorName: '',
            manufacturerCode,
            manufacturerName,
            label,
        };
    }

    const furnizorId = pricing.furnizorId ?? pricing.furnizor_id ?? null;
    const furnizorCode = String(
        pricing.furnizorCode || pricing.furnizor_code || '',
    ).trim();
    const furnizorName = String(
        pricing.furnizorName || pricing.furnizor_name || '',
    ).trim();
    if (furnizorId == null && !furnizorCode && !furnizorName) {
        if (manufacturerName || manufacturerCode) {
            return {
                type: 'manufacturer',
                furnizorId: null,
                furnizorCode: '',
                furnizorName: '',
                manufacturerCode,
                manufacturerName,
                label: manufacturerName || manufacturerCode || 'Producător',
            };
        }
        return null;
    }

    return {
        type: 'furnizor',
        furnizorId,
        furnizorCode,
        furnizorName,
        manufacturerCode: '',
        manufacturerName: '',
        label: furnizorName || furnizorCode || (
            furnizorId != null ? `Furnizor #${furnizorId}` : 'Furnizor'
        ),
    };
}

/**
 * Display labels used to group products under the same furnizor (same as list column).
 * @param {Record<string, unknown>|null|undefined} pricing
 * @param {Record<string, unknown>} [context]
 * @returns {string[]}
 */
export function getPricingMatchLabels(pricing, context = {}) {
    const identity = getFurnizorIdentity(pricing, context);
    if (!identity) {
        return [];
    }
    const labels = [identity.label];
    if (identity.type === 'furnizor') {
        if (identity.furnizorName) {
            labels.push(identity.furnizorName);
        }
        if (identity.furnizorCode) {
            labels.push(identity.furnizorCode);
        }
    } else {
        if (identity.manufacturerName) {
            labels.push(identity.manufacturerName);
        }
        if (identity.manufacturerCode) {
            labels.push(identity.manufacturerCode);
        }
    }
    return [...new Set(
        labels.map((entry) => normalizeMatchLabel(entry)).filter(Boolean),
    )];
}

/**
 * @param {ReturnType<typeof getFurnizorIdentity>} a
 * @param {ReturnType<typeof getFurnizorIdentity>} b
 * @returns {boolean}
 */
export function furnizorIdentitiesMatch(a, b) {
    if (!a || !b) {
        return false;
    }
    // Same display name (list column) — manufacturer-as-furnizor vs explicit furnizor.
    const labelA = normalizeMatchLabel(a.label);
    const labelB = normalizeMatchLabel(b.label);
    if (labelA && labelB && labelA === labelB) {
        return true;
    }

    if (a.type === 'furnizor' && b.type === 'furnizor') {
        if (a.furnizorId != null && b.furnizorId != null) {
            return String(a.furnizorId) === String(b.furnizorId);
        }
        const codeA = normalizeMatchCode(a.furnizorCode);
        const codeB = normalizeMatchCode(b.furnizorCode);
        if (codeA && codeB) {
            return codeA === codeB;
        }
        const nameA = normalizeMatchLabel(a.furnizorName);
        const nameB = normalizeMatchLabel(b.furnizorName);
        return Boolean(nameA && nameB && nameA === nameB);
    }
    if (a.type === 'manufacturer' && b.type === 'manufacturer') {
        const codeA = normalizeMatchCode(a.manufacturerCode);
        const codeB = normalizeMatchCode(b.manufacturerCode);
        if (codeA && codeB) {
            return codeA === codeB;
        }
        const nameA = normalizeMatchLabel(a.manufacturerName);
        const nameB = normalizeMatchLabel(b.manufacturerName);
        return Boolean(nameA && nameB && nameA === nameB);
    }

    // Cross-type: "producător ca furnizor" vs explicit furnizor with the same
    // code/name (import + picker mix often stores the same supplier both ways).
    if (
        (a.type === 'manufacturer' && b.type === 'furnizor')
        || (a.type === 'furnizor' && b.type === 'manufacturer')
    ) {
        const mfr = a.type === 'manufacturer' ? a : b;
        const frz = a.type === 'furnizor' ? a : b;
        const mfrCode = normalizeMatchCode(mfr.manufacturerCode);
        const frzCode = normalizeMatchCode(frz.furnizorCode);
        if (mfrCode && frzCode && mfrCode === frzCode) {
            return true;
        }
        const mfrName = normalizeMatchLabel(mfr.manufacturerName || mfr.label);
        const frzName = normalizeMatchLabel(frz.furnizorName || frz.label);
        if (mfrName && frzName && mfrName === frzName) {
            return true;
        }
    }
    return false;
}

/**
 * @param {Object} item
 * @returns {Record<string, unknown>}
 */
function itemManufacturerContext(item) {
    const detail = item?.pricing_detail || {};
    return {
        manufacturerCode: detail.manufacturerCode
            || detail.manufacturer_code
            || item?.manufacturer_code
            || '',
        manufacturerName: detail.manufacturerName
            || detail.manufacturer_name
            || item?.manufacturer_name
            || '',
    };
}

/**
 * @param {Object} item
 * @returns {Array<Record<string, unknown>>}
 */
function itemFurnizorPricingRows(item) {
    const detail = item?.pricing_detail || {};
    if (Array.isArray(detail.furnizorPricings) && detail.furnizorPricings.length) {
        return detail.furnizorPricings;
    }
    if (Array.isArray(detail.furnizor_pricings) && detail.furnizor_pricings.length) {
        return detail.furnizor_pricings;
    }
    return [detail];
}

/**
 * @param {Object} item
 * @param {ReturnType<typeof getFurnizorIdentity>} identity
 * @returns {boolean}
 */
export function itemHasFurnizorIdentity(item, identity) {
    if (!item || !identity) {
        return false;
    }
    const wantedLabels = new Set(
        getPricingMatchLabels(identity, identity),
    );
    const itemFurnizorLabel = normalizeMatchLabel(item.furnizor);
    if (itemFurnizorLabel && wantedLabels.has(itemFurnizorLabel)) {
        return true;
    }
    const context = itemManufacturerContext(item);
    return itemFurnizorPricingRows(item).some((row) => {
        if (furnizorIdentitiesMatch(getFurnizorIdentity(row, context), identity)) {
            return true;
        }
        return getPricingMatchLabels(row, context).some((label) => wantedLabels.has(label));
    });
}

/**
 * @param {Object} draft
 * @returns {Object|null}
 */
export function draftEntryAsItem(draft) {
    if (!draft?.payload?.product_code) {
        return null;
    }
    const payload = draft.payload;
    const builderForm = draft.builderState?.productBuilderForm;
    let pricingDetail = payload.pricing_detail || null;
    if (!pricingDetail && builderForm) {
        // Minimal shape for peer matching / display when payload lacks detail.
        pricingDetail = {
            manufacturerCode: builderForm.manufacturerCode,
            manufacturerName: builderForm.manufacturerName,
            nomenclatureCode: builderForm.nomenclatureCode,
            furnizorPricings: Array.isArray(builderForm.furnizorPricings)
                ? builderForm.furnizorPricings
                : [],
            transportCostRate: builderForm.transportCostRate,
            currency: builderForm.currency,
            notes: builderForm.notes,
        };
    }
    return {
        product_code: payload.product_code,
        product_name: payload.product_name || payload.name,
        name: payload.product_name || payload.name,
        price: payload.price,
        pricing_detail: pricingDetail,
        has_pricing_detail: Boolean(pricingDetail),
        manufacturer_code: pricingDetail?.manufacturerCode,
        manufacturer_name: pricingDetail?.manufacturerName,
        _draftId: draft.id,
        _fromDraft: true,
    };
}

/**
 * Resolve the working item for a product code (draft overlay preferred).
 * @param {Object} params
 * @param {string} params.productCode
 * @param {Array<Object>} params.items
 * @param {Array<Object>} params.drafts
 * @returns {Object|null}
 */
export function resolvePropagatePeerItem({ productCode, items, drafts }) {
    const code = String(productCode || '').trim();
    if (!code) {
        return null;
    }
    const draft = findDraftByProductCode(drafts, code);
    const live = (items || []).find(
        (item) => String(item?.product_code || '').trim() === code,
    ) || null;
    if (draft) {
        const asItem = draftEntryAsItem(draft);
        if (asItem) {
            return {
                ...live,
                ...asItem,
                // Keep live categories / flags when draft overlay is thin.
                categories: live?.categories || asItem.categories,
                _draftId: draft.id,
                _draftEntry: draft,
            };
        }
    }
    return live;
}

/**
 * @param {Object} params
 * @param {Array<Object>} params.items
 * @param {Array<Object>} [params.drafts]
 * @param {ReturnType<typeof getFurnizorIdentity>} params.identity
 * @param {string} params.excludeProductCode
 * @returns {Array<{
 *   product_code: string,
 *   product_name: string,
 *   price: unknown,
 *   transportCostRate: unknown,
 *   hasDraft: boolean,
 *   item: Object,
 * }>}
 */
export function collectPropagatePeers({
    items = [],
    drafts = [],
    identity,
    excludeProductCode,
}) {
    if (!identity) {
        return [];
    }
    const exclude = String(excludeProductCode || '').trim();
    const codes = new Set();
    const peers = [];

    const consider = (item) => {
        const code = String(item?.product_code || '').trim();
        if (!code || code === exclude || codes.has(code)) {
            return;
        }
        if (!itemHasFurnizorIdentity(item, identity)) {
            return;
        }
        codes.add(code);
        const resolved = resolvePropagatePeerItem({
            productCode: code,
            items,
            drafts,
        }) || item;
        const detail = resolved.pricing_detail || {};
        const pricings = itemFurnizorPricingRows(resolved);
        const context = itemManufacturerContext(resolved);
        const wantedLabels = new Set(getPricingMatchLabels(identity, identity));
        const matched = pricings.find((row) => {
            if (furnizorIdentitiesMatch(getFurnizorIdentity(row, context), identity)) {
                return true;
            }
            return getPricingMatchLabels(row, context).some((label) => wantedLabels.has(label));
        }) || detail;
        peers.push({
            product_code: code,
            product_name: resolved.product_name || resolved.name || code,
            price: resolved.price,
            transportCostRate: matched?.transportCostRate
                ?? detail.transportCostRate
                ?? null,
            hasDraft: Boolean(resolved._draftId || findDraftByProductCode(drafts, code)),
            item: resolved,
        });
    };

    for (const item of items || []) {
        consider(item);
    }
    for (const draft of drafts || []) {
        const asItem = draftEntryAsItem(draft);
        if (asItem) {
            consider(asItem);
        }
    }

    peers.sort((a, b) => String(a.product_code).localeCompare(String(b.product_code), 'ro'));
    return peers;
}

/**
 * Snapshot values to copy from the source builder form (active furnizor root).
 * @param {Record<string, unknown>} form
 * @param {string[]} fieldKeys
 * @returns {Record<string, unknown>}
 */
export function extractPropagateValuesFromForm(form, fieldKeys = []) {
    const values = {};
    for (const key of fieldKeys) {
        if (key === 'transportCostRate') {
            values.transportCostRate = form.transportCostRate;
            values.transportCostBreakdown = cloneTransportCostBreakdown(
                form.transportCostBreakdown,
            );
            continue;
        }
        if (key === 'stampDuty') {
            values.stampDutyAmount = form.stampDutyAmount ?? '0';
            values.stampDutyCurrency = form.stampDutyCurrency || 'ron';
            values.stampDutyBreakdown = cloneStampDutyBreakdown(
                form.stampDutyBreakdown,
            );
            continue;
        }
        if (key === 'nomenclatureCode') {
            values.nomenclatureCode = form.nomenclatureCode ?? '';
            continue;
        }
        if (key === 'markupActual') {
            values.markupActual = form.markupActual ?? '';
            continue;
        }
        if (key === 'lockMarkupToReference') {
            values.lockMarkupToReference = Boolean(form.lockMarkupToReference);
            continue;
        }
        values[key] = form[key];
    }
    return values;
}

/**
 * Default-checked field keys = changed vs session baseline on active tab.
 * Currency also checks the open-session baseline: after FX convert, the session
 * start baseline currency is updated to match the form, so only the open
 * snapshot still reflects the real currency change.
 * @param {Object} params
 * @param {Record<string, unknown>} params.form
 * @param {Record<string, unknown>|null|undefined} params.baseline
 * @param {Record<string, unknown>|null|undefined} [params.openBaseline]
 * @param {string} [params.nomenclatureBaseline]
 * @returns {string[]}
 */
export function detectDefaultPropagateFieldKeys({
    form,
    baseline,
    openBaseline = null,
    nomenclatureBaseline = '',
}) {
    const selected = [];
    for (const field of PROPAGATE_FIELD_DEFINITIONS) {
        if (field.key === 'nomenclatureCode') {
            const current = String(form?.nomenclatureCode ?? '').trim();
            const previous = String(nomenclatureBaseline ?? '').trim();
            if (current !== previous) {
                selected.push(field.key);
            }
            continue;
        }
        if (field.key === 'transportCostRate') {
            if (!baseline) {
                continue;
            }
            const rateChanged = !pricingFieldValuesEqual(
                'transportCostRate',
                form?.transportCostRate,
                baseline.transportCostRate,
            );
            const breakdownChanged = Object.prototype.hasOwnProperty.call(
                baseline,
                'transportCostBreakdown',
            ) && !pricingFieldValuesEqual(
                'transportCostBreakdown',
                form?.transportCostBreakdown,
                baseline.transportCostBreakdown,
            );
            if (rateChanged || breakdownChanged) {
                selected.push(field.key);
            }
            continue;
        }
        if (field.key === 'stampDuty') {
            if (!baseline) {
                continue;
            }
            const amountChanged = !pricingFieldValuesEqual(
                'stampDutyAmount',
                form?.stampDutyAmount,
                baseline.stampDutyAmount,
            );
            const currencyChanged = String(form?.stampDutyCurrency || '')
                !== String(baseline.stampDutyCurrency || '');
            const breakdownChanged = Object.prototype.hasOwnProperty.call(
                baseline,
                'stampDutyBreakdown',
            ) && !pricingFieldValuesEqual(
                'stampDutyBreakdown',
                form?.stampDutyBreakdown,
                baseline.stampDutyBreakdown,
            );
            if (amountChanged || currencyChanged || breakdownChanged) {
                selected.push(field.key);
            }
            continue;
        }
        if (field.key === 'currency') {
            const differsFrom = (snap) => (
                Boolean(snap)
                && !pricingFieldValuesEqual(
                    'currency',
                    form?.currency,
                    snap.currency,
                )
            );
            if (differsFrom(baseline) || differsFrom(openBaseline)) {
                selected.push(field.key);
            }
            continue;
        }
        if (!baseline) {
            continue;
        }
        // Skip fields absent from older baselines — missing keys would
        // false-positive (e.g. paymentTermDays "30" vs undefined).
        if (!Object.prototype.hasOwnProperty.call(baseline, field.key)) {
            continue;
        }
        if (!pricingFieldValuesEqual(field.key, form?.[field.key], baseline[field.key])) {
            selected.push(field.key);
        }
    }
    return selected;
}

/**
 * Human-readable current value for a propagate field checkbox tooltip.
 * @param {string} fieldKey
 * @param {Record<string, unknown>|null|undefined} form
 * @returns {string}
 */
export function formatPropagateFieldCurrentValue(fieldKey, form) {
    if (!form || typeof form !== 'object') {
        return '—';
    }
    if (fieldKey === 'transportCostRate') {
        return formatPropagateTransportDisplay(form.transportCostRate);
    }
    if (fieldKey === 'currency') {
        const code = String(form.currency || '').trim().toLowerCase();
        if (!code) {
            return '—';
        }
        const option = CURRENCY_OPTIONS.find(
            (row) => String(row.value).toLowerCase() === code,
        );
        if (option?.title) {
            return option.title;
        }
        if (code === 'euro' || code === 'eur') {
            return 'EUR';
        }
        return code.toUpperCase();
    }
    if (fieldKey === 'paymentTermDays') {
        const days = String(form.paymentTermDays ?? '').trim();
        return days || '—';
    }
    if (fieldKey === 'notes') {
        const notes = String(form.notes ?? '').trim();
        if (!notes) {
            return '—';
        }
        return notes.length > 120 ? `${notes.slice(0, 117)}…` : notes;
    }
    if (fieldKey === 'markupReference' || fieldKey === 'markupActual') {
        // Builder / peer-as-form store percentage points (80), not ratios (0.8).
        // formatPercentValue multiplies by 100 → "8.000%" for 80 (ro-RO thousands).
        return formatPropagatePercentPointsDisplay(form[fieldKey]);
    }
    if (fieldKey === 'lockMarkupToReference') {
        return form.lockMarkupToReference ? 'Da' : 'Nu';
    }
    if (fieldKey === 'stampDuty') {
        const amountRaw = form.stampDutyAmount;
        if (amountRaw === null || amountRaw === undefined || amountRaw === '') {
            return '—';
        }
        const amount = parseNumericInput(amountRaw);
        if (!Number.isFinite(amount)) {
            return '—';
        }
        const currencyCode = String(
            form.stampDutyCurrency || form.currency || 'ron',
        ).trim().toLowerCase();
        const currencyOption = CURRENCY_OPTIONS.find(
            (row) => String(row.value).toLowerCase() === currencyCode,
        );
        const currencyLabel = currencyOption?.title
            || (currencyCode === 'euro' || currencyCode === 'eur'
                ? 'EUR'
                : (currencyCode.toUpperCase() || 'RON'));
        return `${formatNumericInputValue(amount, 4)} ${currencyLabel}`;
    }
    if (fieldKey === 'nomenclatureCode') {
        const code = String(form.nomenclatureCode ?? '').trim();
        return code || '—';
    }
    const raw = form[fieldKey];
    if (raw === null || raw === undefined || raw === '') {
        return '—';
    }
    return String(raw);
}

/**
 * Activate the matching furnizor tab on a builder form.
 * @param {Record<string, unknown>} form
 * @param {ReturnType<typeof getFurnizorIdentity>} identity
 * @returns {Record<string, unknown>|null} matched pricing row
 */
export function activateMatchingFurnizorTab(form, identity) {
    const pricings = Array.isArray(form?.furnizorPricings) ? form.furnizorPricings : [];
    const context = {
        manufacturerCode: form?.manufacturerCode,
        manufacturerName: form?.manufacturerName,
    };
    const wantedLabels = new Set(getPricingMatchLabels(identity, identity));
    const matched = pricings.find((row) => {
        const rowIdentity = getFurnizorIdentity(row, context);
        if (furnizorIdentitiesMatch(rowIdentity, identity)) {
            return true;
        }
        return getPricingMatchLabels(row, context).some((label) => wantedLabels.has(label));
    }) || null;
    if (!matched) {
        return null;
    }
    form.activeFurnizorPricingKey = matched.clientKey;
    applyFurnizorPricingToRoot(form, matched);
    return matched;
}

/**
 * Apply extracted propagate values onto the active root form buffer.
 * @param {Record<string, unknown>} form
 * @param {string[]} fieldKeys
 * @param {Record<string, unknown>} values
 */
export function applyPropagateValuesToForm(form, fieldKeys, values) {
    const lockSelected = fieldKeys.includes('lockMarkupToReference');
    for (const key of fieldKeys) {
        if (key === 'lockMarkupToReference') {
            // Applied last so a co-selected markupActual cannot clear it mid-loop.
            continue;
        }
        if (key === 'transportCostRate') {
            form.transportCostRate = values.transportCostRate ?? '';
            form.transportCostBreakdown = cloneTransportCostBreakdown(
                values.transportCostBreakdown,
            );
            continue;
        }
        if (key === 'stampDuty') {
            form.stampDutyAmount = values.stampDutyAmount ?? '0';
            form.stampDutyCurrency = values.stampDutyCurrency || 'ron';
            form.stampDutyBreakdown = cloneStampDutyBreakdown(
                values.stampDutyBreakdown,
            );
            continue;
        }
        if (key === 'nomenclatureCode') {
            form.nomenclatureCode = values.nomenclatureCode ?? '';
            continue;
        }
        if (key === 'markupActual') {
            // Clear sticky list override so the copied markup drives pret listă.
            applyPricingFieldEdit(form, 'markupActual', values.markupActual ?? '');
            continue;
        }
        if (Object.prototype.hasOwnProperty.call(values, key)) {
            form[key] = values[key];
        }
    }
    if (lockSelected) {
        form.lockMarkupToReference = Boolean(values.lockMarkupToReference);
        if (form.lockMarkupToReference) {
            syncMarkupActualToReference(form);
        }
    }
}

/**
 * @param {Object} params
 * @param {Object} params.item
 * @param {ReturnType<typeof getFurnizorIdentity>} params.identity
 * @param {string[]} params.fieldKeys
 * @param {Record<string, unknown>} params.values
 * @param {Array<Object|number>} [params.priceListIds]
 * @param {Object} [params.pricingSettings]
 * @returns {{ ok: true, payload: Object, builderState: Object, item: Object }
 *   | { ok: false, reason: string, item: Object }}
 */
export function buildPropagateUpdateForItem({
    item,
    identity,
    fieldKeys,
    values,
    priceListIds = [],
    pricingSettings = DEFAULT_PRICING_SETTINGS,
}) {
    if (!item?.product_code) {
        return { ok: false, reason: 'Produs fără cod.', item };
    }
    if (!Array.isArray(fieldKeys) || !fieldKeys.length) {
        return { ok: false, reason: 'Niciun câmp de propagat.', item };
    }

    const hasPricingDetail = Boolean(item.pricing_detail || item.has_pricing_detail);
    const categories = getProductMainCategories(item);
    let productBuilderForm;
    let settings;

    const draftForm = item._draftEntry?.builderState?.productBuilderForm;
    const existingBuilderState = item._draftEntry?.builderState || null;
    if (draftForm) {
        productBuilderForm = cloneDeep(draftForm);
        settings = item._draftEntry?.builderState?.pricingSettings || {};
    } else if (hasPricingDetail) {
        ({ productBuilderForm, pricingSettings: settings } = pricingDetailToBuilderForm(
            item.pricing_detail,
            item,
        ));
    } else {
        ({ productBuilderForm, pricingSettings: settings } = legacyProductToBuilderForm(
            item,
            categories,
        ));
    }

    const matched = activateMatchingFurnizorTab(productBuilderForm, identity);
    if (!matched) {
        return {
            ok: false,
            reason: 'Produsul nu are tab-ul furnizorului selectat.',
            item,
        };
    }

    const mergedSettings = {
        ...DEFAULT_PRICING_SETTINGS,
        ...settings,
        ...pricingSettings,
    };

    // Capture Valoare veche / orange baselines BEFORE applying propagate values.
    const draftBaselines = mergeDraftPricingBaselinesWithExisting(
        buildDraftPricingBaselinesFromPreEditForm(productBuilderForm, mergedSettings),
        existingBuilderState,
    );

    applyPropagateValuesToForm(productBuilderForm, fieldKeys, values);
    persistActiveFurnizorPricingFromRoot(productBuilderForm);

    const needsPricingRecalc = fieldKeys.some(
        (key) => getPropagateField(key)?.needsPricingRecalc,
    );

    const calculatorInput = builderFormToCalculatorInput(productBuilderForm);
    let computedProduct = calculateProductPricing(calculatorInput, mergedSettings);

    if (item.product_code) {
        productBuilderForm.overrideProductCode = true;
        productBuilderForm.overrideProductCodeValue = item.product_code;
        computedProduct.productCode = item.product_code;
    }

    if (
        needsPricingRecalc
        && parseNumericInput(computedProduct.listPriceRonIncVatFinal) <= 0
    ) {
        const currentPrice = parseNumericInput(item.price);
        if (currentPrice > 0) {
            computedProduct = {
                ...computedProduct,
                listPriceRonIncVatFinal: currentPrice,
            };
        }
    }

    if (needsPricingRecalc) {
        productBuilderForm.listPriceRonIncVatReference = String(
            computedProduct.listPriceRonIncVatFinal ?? '',
        );
        persistActiveFurnizorPricingFromRoot(productBuilderForm);
    }

    const categoryAssignments = buildCategoryAssignmentsFromSystems(
        Array.isArray(productBuilderForm.systems) ? productBuilderForm.systems : [],
        productBuilderForm.systemSubcategories || [],
    );

    const payload = builderFormToSavePayload({
        form: productBuilderForm,
        settings: mergedSettings,
        computedProduct,
        categories: categoryAssignments.map((assignment) => assignment.main_category),
        categoryAssignments,
        priceListIds: priceListIds.length ? priceListIds : [],
        oldProductCode: item.product_code,
    });

    if (!needsPricingRecalc) {
        payload.price = item.price;
        if (payload.pricing_detail) {
            payload.pricing_detail.listPriceRonIncVatFinal = item.price;
            if (Array.isArray(payload.pricing_detail.furnizorPricings)) {
                payload.pricing_detail.furnizorPricings = payload.pricing_detail.furnizorPricings.map(
                    (row) => ({
                        ...row,
                        listPriceRonIncVatFinal: row.isDefault
                            ? item.price
                            : row.listPriceRonIncVatFinal,
                    }),
                );
            }
        }
    }

    return {
        ok: true,
        payload,
        builderState: {
            productBuilderForm: cloneDeep(productBuilderForm),
            pricingSettings: cloneDeep(mergedSettings),
            ...draftBaselines,
            // Lock-to-reference wins over a co-selected markupActual (lock forces sync).
            ...(fieldKeys.includes('lockMarkupToReference')
                && Boolean(values.lockMarkupToReference)
                ? { markupActualManual: false }
                : (fieldKeys.includes('markupActual')
                    ? { markupActualManual: true }
                    : (existingBuilderState?.markupActualManual !== undefined
                        ? { markupActualManual: existingBuilderState.markupActualManual }
                        : {}))),
            ...(existingBuilderState?.manuallyEditedPricingFields
                ? {
                    manuallyEditedPricingFields: cloneDeep(
                        existingBuilderState.manuallyEditedPricingFields,
                    ),
                }
                : {}),
            ...(existingBuilderState?.newFurnizorKeysPendingFirstSave
                ? {
                    newFurnizorKeysPendingFirstSave: {
                        ...existingBuilderState.newFurnizorKeysPendingFirstSave,
                    },
                }
                : {}),
            ...(existingBuilderState?.unpublishedNewFurnizorKeys
                ? {
                    unpublishedNewFurnizorKeys: {
                        ...existingBuilderState.unpublishedNewFurnizorKeys,
                    },
                }
                : {}),
        },
        item,
        previousPrice: item.price,
        nextPrice: payload.price,
    };
}

/**
 * Build draft entries for selected peer products.
 * @param {Object} params
 * @param {Array<Object>} params.peerItems
 * @param {ReturnType<typeof getFurnizorIdentity>} params.identity
 * @param {string[]} params.fieldKeys
 * @param {Record<string, unknown>} params.values
 * @param {Array<Object>} params.existingItems
 * @param {Array<Object|number>} [params.priceListIds]
 * @param {Object} [params.pricingSettings]
 * @returns {{
 *   drafts: Array<Object>,
 *   skipped: Array<{ item: Object, reason: string }>,
 *   results: Array<Object>,
 * }}
 */
export function buildPropagateDrafts({
    peerItems,
    identity,
    fieldKeys,
    values,
    existingItems = [],
    priceListIds = [],
    pricingSettings,
}) {
    const existingCodes = (existingItems || []).map((item) => item.product_code);
    const drafts = [];
    const skipped = [];
    const results = [];

    for (const item of peerItems || []) {
        const built = buildPropagateUpdateForItem({
            item,
            identity,
            fieldKeys,
            values,
            priceListIds,
            pricingSettings,
        });
        if (!built.ok) {
            skipped.push({ item, reason: built.reason });
            continue;
        }
        results.push(built);
        const action = resolveDraftAction(built.payload, existingCodes);
        const entry = createDraftEntry({
            action,
            payload: built.payload,
            builderState: built.builderState,
            sourceProductCode: item.product_code,
            previousPrice: built.previousPrice,
            updateMode: 'builder',
        });
        if (item._draftId) {
            entry.id = item._draftId;
        }
        drafts.push(entry);
    }

    return { drafts, skipped, results };
}

/**
 * Empty filter criteria for the propagate peers dialog (furnizor is fixed).
 * @returns {{
 *   search: string,
 *   systems: string[],
 *   systemSubcategory: string|null,
 *   manufacturerCode: string|null,
 *   chapterCode: string[],
 *   subchapterCode: string|null,
 * }}
 */
export function emptyPropagateFilterCriteria() {
    return {
        search: '',
        systems: [],
        systemSubcategory: null,
        manufacturerCode: null,
        chapterCode: [],
        subchapterCode: null,
    };
}

/**
 * True when criteria need a full furnizor fetch + client-side match
 * (API cannot express multi-system / subcategory / multi-chapter precisely).
 * @param {Record<string, unknown>} criteria
 * @returns {boolean}
 */
export function propagateCriteriaNeedsFullFetch(criteria = {}) {
    const c = criteria || {};
    const systems = (
        Array.isArray(c.systems) ? c.systems : (c.systems ? [c.systems] : [])
    ).filter((entry) => String(entry || '').trim());
    if (systems.length > 0) {
        return true;
    }
    if (String(c.systemSubcategory || '').trim()) {
        return true;
    }
    const chapters = (
        Array.isArray(c.chapterCode) ? c.chapterCode : (c.chapterCode ? [c.chapterCode] : [])
    ).filter((entry) => String(entry || '').trim());
    if (chapters.length > 0) {
        return true;
    }
    if (String(c.manufacturerCode || '').trim()) {
        return true;
    }
    return false;
}

/**
 * Build search haystack for a peer/item — same fields as price-list list search:
 * cod produs, denumire, cod produs furnizor, denumire produs furnizor.
 * @param {Object} peer
 * @returns {string}
 */
export function buildPropagatePeerSearchHaystack(peer) {
    const item = peer?.item || peer || {};
    const detail = item.pricing_detail || {};
    const pricings = itemFurnizorPricingRows(item);
    const manufacturerCodes = [
        item.manufacturer_product_code,
        item.manufacturerProductCode,
        detail.manufacturerProductCode,
        detail.manufacturer_product_code,
        ...pricings.map((row) => row?.manufacturerProductCode ?? row?.manufacturer_product_code),
    ];
    const manufacturerNames = [
        item.manufacturer_product_name,
        item.manufacturerProductName,
        detail.manufacturerProductName,
        detail.manufacturer_product_name,
        ...pricings.map((row) => row?.manufacturerProductName ?? row?.manufacturer_product_name),
    ];
    return [
        peer?.product_code,
        item.product_code,
        peer?.product_name,
        item.product_name,
        item.name,
        ...manufacturerCodes,
        ...manufacturerNames,
    ]
        .map((entry) => String(entry || '').trim())
        .filter(Boolean)
        .join(' ');
}

/**
 * Filter peers by search text (cod / denumire + cod/denumire produs furnizor),
 * matching the price-list products list search.
 * Structural filters (sistem / producător / categorie) are applied by the caller
 * via itemMatchesBulkCriteria to avoid a circular import with priceListBulkApply.
 * @param {Array<Object>} peers
 * @param {string|Record<string, unknown>} searchOrCriteria
 * @returns {Array<Object>}
 */
export function filterPropagatePeers(peers, searchOrCriteria) {
    const criteria = typeof searchOrCriteria === 'string'
        ? { search: searchOrCriteria }
        : (searchOrCriteria || {});
    const needle = String(criteria.search || '').trim();
    if (!needle) {
        return peers || [];
    }
    return (peers || []).filter((peer) => (
        includesFolded(buildPropagatePeerSearchHaystack(peer), needle)
    ));
}

/**
 * Apply search + bulk-style structural criteria to propagate peers.
 * @param {Array<Object>} peers
 * @param {Record<string, unknown>} criteria
 * @param {(item: Object, criteria: Record<string, unknown>) => boolean} itemMatches
 * @returns {Array<Object>}
 */
export function filterPropagatePeersWithCriteria(peers, criteria = {}, itemMatches = null) {
    const list = filterPropagatePeers(peers, criteria);
    if (typeof itemMatches !== 'function' || !propagateCriteriaNeedsFullFetch(criteria)) {
        return list;
    }
    return list.filter((peer) => {
        const item = peer?.item || peer;
        if (!item) {
            return false;
        }
        return itemMatches(item, {
            ...criteria,
            search: '',
            furnizorLabel: null,
        });
    });
}

/**
 * Resolve the matched furnizor pricing row for a peer item.
 * @param {Object} item
 * @param {ReturnType<typeof getFurnizorIdentity>} identity
 * @returns {Record<string, unknown>|null}
 */
export function resolvePeerMatchedPricing(item, identity) {
    if (!item || !identity) {
        return null;
    }
    const detail = item.pricing_detail || {};
    const pricings = itemFurnizorPricingRows(item);
    const context = itemManufacturerContext(item);
    const wantedLabels = new Set(getPricingMatchLabels(identity, identity));
    return pricings.find((row) => {
        if (furnizorIdentitiesMatch(getFurnizorIdentity(row, context), identity)) {
            return true;
        }
        return getPricingMatchLabels(row, context).some((label) => wantedLabels.has(label));
    }) || (Object.keys(detail).length ? detail : null);
}

/**
 * Snapshot peer+matched-furnizor fields into a form-like object for display.
 * @param {Object} item
 * @param {ReturnType<typeof getFurnizorIdentity>} identity
 * @returns {Record<string, unknown>}
 */
export function peerItemAsPropagateForm(item, identity) {
    const detail = item?.pricing_detail || {};
    const matched = resolvePeerMatchedPricing(item, identity) || {};
    // API stores markup as ratios (0.8); builder form uses percent points (80).
    const markupReferenceRaw = matched.markupReference ?? detail.markupReference ?? '';
    const markupActualRaw = matched.markupActual ?? detail.markupActual ?? '';
    return {
        transportCostRate: matched.transportCostRate ?? detail.transportCostRate ?? '',
        transportCostBreakdown: matched.transportCostBreakdown
            ?? detail.transportCostBreakdown
            ?? null,
        currency: matched.currency ?? detail.currency ?? '',
        paymentTermDays: matched.paymentTermDays ?? detail.paymentTermDays ?? '',
        notes: matched.notes ?? detail.notes ?? '',
        markupReference: markupReferenceRaw === '' || markupReferenceRaw == null
            ? ''
            : ratioToPercentageInput(markupReferenceRaw),
        markupActual: markupActualRaw === '' || markupActualRaw == null
            ? ''
            : ratioToPercentageInput(markupActualRaw),
        lockMarkupToReference: Boolean(
            matched.lockMarkupToReference ?? detail.lockMarkupToReference,
        ),
        stampDutyAmount: matched.stampDutyAmount ?? detail.stampDutyAmount ?? '0',
        stampDutyCurrency: matched.stampDutyCurrency
            ?? detail.stampDutyCurrency
            ?? matched.currency
            ?? detail.currency
            ?? 'ron',
        stampDutyBreakdown: matched.stampDutyBreakdown
            ?? detail.stampDutyBreakdown
            ?? null,
        nomenclatureCode: detail.nomenclatureCode
            ?? detail.nomenclature_code
            ?? item?.nomenclature_code
            ?? '',
    };
}

/**
 * Human-readable current value of a propagate field on a peer product.
 * @param {string} fieldKey
 * @param {Object} item
 * @param {ReturnType<typeof getFurnizorIdentity>} identity
 * @returns {string}
 */
export function formatPropagatePeerFieldValue(fieldKey, item, identity) {
    return formatPropagateFieldCurrentValue(
        fieldKey,
        peerItemAsPropagateForm(item, identity),
    );
}

/**
 * Build one table preview row (Valoare curentă / nouă + preț) for a peer.
 * @param {Object} params
 * @param {Object} params.peer collectPropagatePeers entry
 * @param {string[]} params.fieldKeys
 * @param {Record<string, unknown>} params.sourceForm
 * @param {ReturnType<typeof getFurnizorIdentity>} params.identity
 * @param {Record<string, unknown>} [params.values]
 * @param {Object} [params.pricingSettings]
 * @param {Array<Object|number>} [params.priceListIds]
 * @returns {Object}
 */
export function buildPropagatePreviewRow({
    peer,
    fieldKeys = [],
    sourceForm,
    identity,
    values = null,
    pricingSettings = DEFAULT_PRICING_SETTINGS,
    priceListIds = [],
}) {
    const item = peer?.item || peer;
    const keys = Array.isArray(fieldKeys) ? fieldKeys.filter(Boolean) : [];
    const previousParts = keys.map((key) => {
        const label = getPropagateField(key)?.label || key;
        const value = formatPropagatePeerFieldValue(key, item, identity);
        return keys.length > 1 ? `${label}: ${value}` : value;
    });
    const nextParts = keys.map((key) => {
        const label = getPropagateField(key)?.label || key;
        const value = formatPropagateFieldCurrentValue(key, sourceForm);
        return keys.length > 1 ? `${label}: ${value}` : value;
    });

    let previousPrice = peer?.price ?? item?.price;
    let nextPrice = previousPrice;
    const needsPricingRecalc = keys.some(
        (key) => getPropagateField(key)?.needsPricingRecalc,
    );
    if (needsPricingRecalc && keys.length && identity && item) {
        const propagateValues = values || extractPropagateValuesFromForm(
            sourceForm || {},
            keys,
        );
        const built = buildPropagateUpdateForItem({
            item,
            identity,
            fieldKeys: keys,
            values: propagateValues,
            priceListIds,
            pricingSettings,
        });
        if (built.ok) {
            previousPrice = built.previousPrice;
            nextPrice = built.nextPrice;
        }
    }

    return {
        product_code: peer?.product_code || item?.product_code || '',
        product_name: peer?.product_name
            || item?.product_name
            || item?.name
            || '',
        previous_value: previousParts.length ? previousParts.join('; ') : '—',
        next_value: nextParts.length ? nextParts.join('; ') : '—',
        previous_price: previousPrice,
        next_price: nextPrice,
        price_changed: Number(previousPrice) !== Number(nextPrice),
        hasDraft: Boolean(peer?.hasDraft),
        transportCostRate: peer?.transportCostRate ?? null,
        price: peer?.price ?? item?.price,
    };
}

/**
 * Build preview rows for the current page of peers.
 * @param {Object} params
 * @param {Array<Object>} params.peers
 * @param {string[]} params.fieldKeys
 * @param {Record<string, unknown>} params.sourceForm
 * @param {ReturnType<typeof getFurnizorIdentity>} params.identity
 * @param {Record<string, unknown>} [params.values]
 * @param {Object} [params.pricingSettings]
 * @param {Array<Object|number>} [params.priceListIds]
 * @returns {Array<Object>}
 */
export function buildPropagatePreviewRows(params = {}) {
    const peers = Array.isArray(params.peers) ? params.peers : [];
    return peers.map((peer) => buildPropagatePreviewRow({
        ...params,
        peer,
    }));
}

/**
 * Format a transport rate for display in the peer list.
 * @param {unknown} value
 * @returns {string}
 */
export function formatPropagateTransportDisplay(value) {
    if (value === null || value === undefined || value === '') {
        return '—';
    }
    const numeric = parseNumericInput(value);
    const percent = Math.abs(numeric) <= 1.5 ? numeric * 100 : numeric;
    return `${roundMoney(percent, 2)}%`;
}

/**
 * Format builder percent-point fields (adaos 80 → "80%") for propagate UI.
 * @param {unknown} value
 * @returns {string}
 */
export function formatPropagatePercentPointsDisplay(value) {
    if (value === null || value === undefined || value === '') {
        return '—';
    }
    const numeric = parseNumericInput(value);
    if (!Number.isFinite(numeric)) {
        return '—';
    }
    const text = formatNumericInputValue(numeric, 2);
    return text ? `${text}%` : '—';
}
